import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '../utils/api';

const T = {
  cardBg:        'rgba(40,25,15,0.95)',
  border:        '#5a3a1a',
  text:          '#f5deb3',
  textSecondary: '#a08060',
  gold:          '#ffd700',
  amber:         '#c8850f',
  inputBg:       '#28190f',
  inputBorder:   '#5a3a1a',
  btnPrimary:    'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
  btnDanger:     '#e74c3c',
  successText:   '#2ecc71',
  errorText:     '#e74c3c',
  tableRowEven:  'rgba(40,25,15,0.6)',
  tableRowOdd:   'rgba(30,18,10,0.6)',
  tableHeaderBg: 'rgba(30,18,10,0.9)',
} as const;

interface Subscriber {
  id: number;
  email: string;
  subscribed_at: string;
}

const NewsletterAdminPage: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const messageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (message) {
      messageTimeoutRef.current = setTimeout(() => setMessage(null), 5000);
    }
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, [message]);

  const fetchSubscribers = async () => {
    try {
      const res = await apiCall('/api/newsletter/subscribers');
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setSubscribers(data.subscribers);
    } catch {
      setMessage({ type: 'error', text: 'Failed to load subscribers' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const handleDelete = async (id: number, email: string) => {
    if (!window.confirm(`Remove ${email} from the mailing list?`)) return;
    try {
      const res = await apiCall(`/api/newsletter/subscribers/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      setSubscribers(prev => prev.filter(s => s.id !== id));
      setMessage({ type: 'success', text: `Removed ${email}` });
    } catch {
      setMessage({ type: 'error', text: 'Failed to remove subscriber' });
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setMessage({ type: 'error', text: 'Subject and body are required' });
      return;
    }
    if (!window.confirm(`Send this newsletter to ${subscribers.length} subscriber${subscribers.length !== 1 ? 's' : ''}?`)) return;

    setSending(true);
    try {
      const res = await apiCall('/api/newsletter/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: subject.trim(),
          html: `<div style="font-family: Georgia, serif; max-width: 600px; margin: 0 auto; background: #1a1a2e; color: #f5deb3; padding: 40px; border-radius: 12px;">
            <h1 style="color: #f5deb3; text-align: center; font-size: 2rem; letter-spacing: 4px;">RODEOKE</h1>
            <p style="color: #d4a574; text-align: center; font-style: italic; font-size: 1.2rem;">Live Band Country Karaoke</p>
            <hr style="border: none; border-top: 1px solid #654321; margin: 30px 0;" />
            <div style="line-height: 1.8; color: #b8a082;">${body.trim().replace(/\n/g, '<br/>')}</div>
            <p style="color: #d4a574; margin-top: 30px;">- The Rodeoke Band</p>
          </div>`
        }),
      });
      if (!res.ok) throw new Error('Failed to send');
      const data = await res.json();
      setMessage({ type: 'success', text: `Sent to ${data.sent} subscriber${data.sent !== 1 ? 's' : ''}${data.failed ? `, ${data.failed} failed` : ''}` });
      setSubject('');
      setBody('');
    } catch {
      setMessage({ type: 'error', text: 'Failed to send newsletter' });
    } finally {
      setSending(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    backgroundColor: T.inputBg,
    border: `1px solid ${T.inputBorder}`,
    color: T.text,
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnStyle: React.CSSProperties = {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#fff',
    background: T.btnPrimary,
  };

  return (
    <div style={{ minHeight: '100vh', padding: '32px 16px', maxWidth: '900px', margin: '0 auto' }}>
      <h1 style={{ color: T.gold, textAlign: 'center', fontSize: '28px', marginBottom: '24px' }}>
        Newsletter Admin
      </h1>

      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '16px',
          borderRadius: '6px',
          backgroundColor: message.type === 'success' ? 'rgba(46,204,113,0.15)' : 'rgba(231,76,60,0.15)',
          border: `1px solid ${message.type === 'success' ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)'}`,
          color: message.type === 'success' ? T.successText : T.errorText,
        }}>
          {message.text}
        </div>
      )}

      {/* Subscribers section */}
      <div style={{
        background: T.cardBg,
        border: `1px solid ${T.border}`,
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
      }}>
        <h2 style={{ color: T.gold, fontSize: '20px', marginTop: 0, marginBottom: '16px' }}>
          Subscribers ({subscribers.length})
        </h2>

        {loading ? (
          <p style={{ color: T.textSecondary }}>Loading...</p>
        ) : subscribers.length === 0 ? (
          <p style={{ color: T.textSecondary, fontStyle: 'italic' }}>No subscribers yet</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: T.tableHeaderBg }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: T.amber, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Email</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: T.amber, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>Signed Up</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: T.amber, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}></th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub, i) => (
                <tr key={sub.id} style={{ backgroundColor: i % 2 === 0 ? T.tableRowEven : T.tableRowOdd }}>
                  <td style={{ padding: '10px 12px', color: T.text, fontSize: '14px' }}>{sub.email}</td>
                  <td style={{ padding: '10px 12px', color: T.textSecondary, fontSize: '13px' }}>
                    {new Date(sub.subscribed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <button
                      onClick={() => handleDelete(sub.id, sub.email)}
                      style={{
                        padding: '4px 12px',
                        fontSize: '12px',
                        background: 'none',
                        border: `1px solid ${T.btnDanger}`,
                        color: T.btnDanger,
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Send newsletter section */}
      <div style={{
        background: T.cardBg,
        border: `1px solid ${T.border}`,
        borderRadius: '8px',
        padding: '20px',
      }}>
        <h2 style={{ color: T.gold, fontSize: '20px', marginTop: 0, marginBottom: '16px' }}>
          Send Newsletter
        </h2>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', color: T.amber, fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="e.g. Rodeoke is back this Friday!"
            style={inputStyle}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '4px', color: T.amber, fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Message
          </label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message here... (plain text, line breaks will be preserved)"
            rows={8}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        <button
          onClick={handleSend}
          disabled={sending || !subject.trim() || !body.trim()}
          style={{
            ...btnStyle,
            opacity: sending || !subject.trim() || !body.trim() ? 0.5 : 1,
            cursor: sending ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending...' : `Send to ${subscribers.length} subscriber${subscribers.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

export default NewsletterAdminPage;
